// Which lift a bundle gets — the shared GpuLifter or the myra fork. Pure.
// Config: myraLiftConfig.ts. Rationale:
// ~/comments/aos-threejs-poc/myraLiftGate_chooseLift_subject_rule.py

import type { MyraLift } from "../ogsGpuFlags.ts";
import { MYRA_LIFT_CONFIG } from "./myraLiftConfig.ts";

/** The lifter a branch is built from. `shared` is GpuLifter — every character. */
export type LiftImpl = "shared" | "myra";

export interface LiftChoice {
  impl: LiftImpl;
  /** Why, for the one console line the caller prints. */
  reason: string;
}

/** What identifies a bundle: scene.json's subject, and the slug the URL asked for. */
export interface BundleIdentity {
  subject?: string | null;
  character?: string | null;
}

/** Pick the lift for a bundle. `mode` is the ?myralift flag. */
export function chooseLift(bundle: BundleIdentity, mode: MyraLift = "auto"): LiftChoice {
  const subject = (bundle.subject ?? "").trim();
  const character = (bundle.character ?? "").trim();
  if (mode === "off") return { impl: "shared", reason: "?myralift=0" };
  if (mode === "on") return { impl: "myra", reason: "?myralift=1" };

  const prefix = MYRA_LIFT_CONFIG.subjectPrefixes
    .find((p) => subject.toLowerCase().startsWith(p.toLowerCase()));
  if (prefix) return { impl: "myra", reason: `subject '${subject}'` };
  if (MYRA_LIFT_CONFIG.characters.includes(character as never)) {
    return { impl: "myra", reason: `character '${character}' is pinned in myraLiftConfig.ts` };
  }
  return {
    impl: "shared",
    reason: `subject '${subject || "(none)"}' / character '${character || "(none)"}' is not `
      + `in myraLiftConfig.ts — ?myralift=1 forces the myra lift`,
  };
}

/** One branch's resolved lift, as `OGSBranch.liftKind()` reports it. */
export interface BranchLiftKind { name: string; kind: string }

/** What to print for a bundle that ASKED for the myra lift; null when it did not. */
export function liftReport(
  choice: LiftChoice,
  branches: readonly BranchLiftKind[],
): { text: string; phase: "ok" | "error" } | null {
  if (choice.impl !== "myra") return null;
  // A branch that fell back still lifts, so the report names which ones — a bare
  // "Myra lift" over a shared-rule render is the failure this whole gate is about.
  const missing = branches.filter((b) => !b.kind.includes("myra"));
  if (missing.length) {
    const named = missing.map((b) => `${b.name} (${b.kind})`).join(", ");
    return { text: `Myra lift MISSING on ${named}`, phase: "error" };
  }
  return { text: `Myra lift · ${branches.map((b) => b.name).join(", ")}`, phase: "ok" };
}
